import { Slot } from '@radix-ui/react-slot';
import type { ButtonHTMLAttributes, JSX, ReactNode } from 'react';

import { SpinnerIcon } from '../icons';

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger';
type Size = 'sm' | 'md' | 'lg';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  readonly variant?: Variant;
  readonly size?: Size;
  readonly asChild?: boolean;
  readonly loading?: boolean;
  readonly fullWidth?: boolean;
  readonly children: ReactNode;
}

/**
 * The brand cyan is reserved for the single primary action on a screen, and it
 * carries ink rather than white: the brand is a light colour, so a white label
 * on it reads at 2.7:1. Red only ever means destructive (CLAUDE.md §8.2).
 * Padding uses logical properties so the component is identical in RTL and LTR
 * (CLAUDE.md §9).
 */
const VARIANTS: Record<Variant, string> = {
  primary: 'bg-primary text-on-primary shadow-md hover:bg-primary-hover',
  secondary: 'bg-surface text-text border border-border shadow-sm hover:bg-raised',
  ghost: 'bg-transparent text-muted hover:bg-raised hover:text-text',
  danger: 'bg-danger text-on-accent shadow-sm hover:opacity-90',
};

const SIZES: Record<Size, string> = {
  sm: 'min-h-9 ps-3 pe-3 text-[length:var(--text-caption)]',
  md: 'min-h-11 ps-5 pe-5 text-[length:var(--text-body)]',
  lg: 'min-h-13 ps-7 pe-7 text-[length:var(--text-headline)]',
};

export function Button({
  variant = 'secondary',
  size = 'md',
  asChild = false,
  loading = false,
  fullWidth = false,
  className = '',
  type = 'button',
  disabled = false,
  children,
  ...rest
}: ButtonProps): JSX.Element {
  const Element = asChild ? Slot : 'button';

  return (
    <Element
      // The press is acknowledged on pointer-down, not on release — waiting for
      // the click to show feedback is the moment an interface starts feeling
      // dead. min-h-11 = 44px, the minimum tap target (CLAUDE.md §7.5).
      className={[
        'relative inline-flex touch-manipulation items-center justify-center gap-2',
        'rounded-full font-medium select-none',
        'transition-[transform,background-color,opacity,box-shadow]',
        'duration-[var(--duration-instant)] ease-[var(--ease-out)] active:scale-[0.97]',
        'disabled:pointer-events-none disabled:opacity-45 disabled:shadow-none',
        VARIANTS[variant],
        SIZES[size],
        fullWidth ? 'w-full' : '',
        className,
      ].join(' ')}
      disabled={disabled || loading}
      type={type}
      {...(loading ? { 'aria-busy': true } : {})}
      {...rest}
    >
      {/*
        Radix's Slot merges props onto exactly *one* child, and a sibling —
        even a `null` one — makes it throw. So a slotted button hands its child
        through untouched. It loses nothing: `asChild` is how this renders a
        link, and a link navigates rather than waiting on a mutation.
      */}
      {asChild ? (
        children
      ) : (
        <>
          {loading ? <SpinnerIcon /> : null}
          {children}
        </>
      )}
    </Element>
  );
}
